import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type {
  ApiClient, Kaelteanlage, Kaeltebefund, Kaeltebilanzzeile, KaelteJahresgangwerte, Kaeltekennwert,
  Kaelterueckkuehlkreis, Kaeltestoffwert,
} from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, ExportLeiste, Jahresgang, KpiCard, KpiReihe, LoadingState,
  StatusBadge, Vorbehalte, annahmenVorbehalt, formatiereZahl, type Spalte, type Vorbehalt,
} from '../muster';
import {
  BILANZART, bilanzVeraltet, rechenstand, unbestaetigt, zustandBefund, zustandBilanz,
  zustandHerkunft,
  type KaelteAnnahme, type KaelteBilanzlauf, type KaelteLaufbefund, type KaelteLaufzeile,
  type KaelteseiteMitLauf,
} from './heizung/kaelte';

/**
 * Die Kälte — Bilanzlauf, Anlagen, Stoffwerte, Widersprüche (KG 434).
 *
 * ═══ Warum es diese Seite gibt ════════════════════════════════════════════
 *
 * Der Navigationsknoten hieß „Heizung/Kälte" und hatte fünf Kinder, von
 * denen KEINES Kälte war. Die Kälte lief in drei Strängen mit — Kühllast im
 * Hüllenmodul, Kaltwasser im Heizungsrohrnetz, Rückkühlung als erkannte
 * Systemgruppe ohne Bemessung. In der Oberfläche war sie nirgends (HZ5).
 *
 * ═══ TH-1 (17.10.2026): der Bilanzlauf steht vorn ═════════════════════════
 *
 * Bis dahin zeigte die Seite nur die vier FESTEN Zeilen aus 0775–0778
 * (Kühllast-Lauf 26: 5.311 kW, 107 % Deckung). Der Bilanzlauf von
 * `kaelte-bilanz` rechnet gegen den aktuellen Kühllast-Lauf in drei
 * Lesarten nebeneinander – zwei davon unterdeckt – und stand nirgends.
 * Jetzt trägt er die Seite; die festen Zeilen stehen zugeklappt darunter,
 * als das gekennzeichnet, was sie sind.
 *
 * Die Seite folgt TX-STANDARD 1.0 S1: PageHeader mit Rechenstand,
 * Vorbehalte, höchstens sechs Kennzahlen, DataTable statt roher Tabellen,
 * Fachstatus nach S1.5, Annahmenliste nach S1.6. Exportleiste (PDF KLB,
 * Excel) folgt mit TX1 in TH-2.
 *
 * ═══ Warum die Randbedingung neben jedem Kennwert steht ═══════════════════
 *
 * Ein Datenblatt ist eine AUSLEGUNG, keine Messung. „600 kW" gilt bei 32 °C
 * Lufteintritt, 40 % r. F., 450 m ü. NN und 45/40 °C; für eine andere
 * Lufttemperatur ist eine neue Herstellerselektion nötig. Eine Zahl ohne
 * Gültigkeitsbereich ist von einer gemessenen nicht zu unterscheiden.
 */

interface Rahmen { api: ApiClient; projekt: { id: string } }

/** Mehrzeiliger Text ohne Inline-Stil: je Zeile ein Umbruch. */
function Zeilen({ text }: { text: string | null | undefined }) {
  if (!text) return <>—</>;
  const teile = text.split('\n');
  return (
    <>
      {teile.map((t, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={i}>{t}{i < teile.length - 1 && <br />}</span>
      ))}
    </>
  );
}

const LAUFZEILEN: Spalte<KaelteLaufzeile>[] = [
  { schluessel: 'bilanz', titel: 'Bilanz', immerSichtbar: true,
    wert: (z) => `${BILANZART[z.bilanzart] ?? z.bilanzart} · ${z.bezeichnung}` },
  { schluessel: 'lesart', titel: 'Lesart', wert: (z) => z.variante,
    titelHinweis: 'A, B1, B2: drei Lesarten der installierten Leistung nebeneinander – keine ist „richtig“ (HZK-06).' },
  { schluessel: 'erf', titel: 'erforderlich', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.erforderlichKw },
  { schluessel: 'inst', titel: 'installiert', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.installiertKw },
  { schluessel: 'res', titel: 'Reserve', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.reserveKw },
  { schluessel: 'deck', titel: 'Deckung', einheit: '%', art: 'zahl', nachkomma: 1, wert: (z) => z.deckungProzent },
  { schluessel: 'kap', titel: 'Kapazität', einheit: 'kWh', art: 'zahl', nachkomma: 0, wert: (z) => z.kapazitaetKwh },
  { schluessel: 'dauer', titel: 'Dauer', einheit: 'h', art: 'zahl', nachkomma: 2, wert: (z) => z.dauerH },
  { schluessel: 'stand', titel: 'Stand', art: 'status',
    wert: (z) => zustandBilanz(z.stand).text,
    inhalt: (z) => { const s = zustandBilanz(z.stand); return <StatusBadge klein zustand={s.zustand} text={s.text} />; } },
];

const LAUFBEFUNDE: Spalte<KaelteLaufbefund>[] = [
  { schluessel: 'stand', titel: 'Stand', art: 'status',
    wert: (b) => zustandBefund(b.stand).text,
    inhalt: (b) => { const s = zustandBefund(b.stand); return <StatusBadge klein zustand={s.zustand} text={s.text} />; } },
  { schluessel: 'thema', titel: 'Thema', wert: (b) => b.thema },
  { schluessel: 'titel', titel: 'Befund', wert: (b) => b.titel },
  { schluessel: 'werte', titel: 'Werte und Quellen', wert: (b) => b.werte, inhalt: (b) => <Zeilen text={b.werte} /> },
  { schluessel: 'frage', titel: 'Frage an die Fachplanung', wert: (b) => b.frage ?? '' },
];

const LASTBEFUNDE: Spalte<Kaeltebefund>[] = [
  { schluessel: 'status', titel: 'Stand', art: 'status',
    wert: (f) => f.status,
    inhalt: (f) => (
      <StatusBadge klein
        zustand={f.status !== 'offen' ? 'gut' : f.schwere === 'fehler' ? 'fehler' : 'warnung'}
        text={f.status !== 'offen' ? f.status : f.schwere === 'fehler' ? 'Fehler' : 'Warnung'} />
    ) },
  { schluessel: 'titel', titel: 'Widerspruch', wert: (f) => f.titel },
  { schluessel: 'text', titel: 'Werte und Quellen', wert: (f) => f.text ?? '', inhalt: (f) => <Zeilen text={f.text} /> },
  { schluessel: 'vorschlag', titel: 'Vorschlag', wert: (f) => f.vorschlag ?? '' },
];

const KENNWERTE: Spalte<Kaeltekennwert>[] = [
  { schluessel: 'name', titel: 'Kennwert', immerSichtbar: true, wert: (k) => k.bezeichnung },
  { schluessel: 'fall', titel: 'Betriebsfall', wert: (k) => k.betriebsfall ?? '' },
  { schluessel: 'wert', titel: 'Wert', art: 'zahl', wert: (k) => k.wert,
    inhalt: (k) => (k.wert === null ? (k.textwert ?? '—') : formatiereZahl(k.wert)) },
  { schluessel: 'einheit', titel: 'Einheit', wert: (k) => k.einheit ?? '' },
  { schluessel: 'herkunft', titel: 'Herkunft', art: 'status',
    wert: (k) => zustandHerkunft(k.herkunft).text,
    inhalt: (k) => { const s = zustandHerkunft(k.herkunft); return <StatusBadge klein zustand={s.zustand} text={s.text} />; } },
  { schluessel: 'rand', titel: 'Randbedingung', wert: (k) => k.randbedingung ?? '',
    inhalt: (k) => k.randbedingung ?? (
      <StatusBadge klein zustand="warnung" text="ohne Randbedingung"
        titel="Diese Zahl gilt nirgends nachweisbar." />
    ) },
  { schluessel: 'hinweis', titel: 'Hinweis', wert: (k) => k.hinweis ?? '' },
  { schluessel: 'fundstelle', titel: 'Fundstelle', wert: (k) => k.fundstelle ?? '', versteckt: true },
];

const STOFFWERTE: Spalte<Kaeltestoffwert>[] = [
  { schluessel: 'fluid', titel: 'Fluid', immerSichtbar: true, wert: (s) => s.fluid },
  { schluessel: 'konz', titel: 'Konzentration', einheit: 'Vol.-%', art: 'zahl', nachkomma: 0, wert: (s) => s.konzentrationVolProz },
  { schluessel: 'temp', titel: 'bei', einheit: '°C', art: 'zahl', nachkomma: 1, wert: (s) => s.temperaturC },
  { schluessel: 'rho', titel: 'Dichte', einheit: 'kg/m³', art: 'zahl', nachkomma: 2, wert: (s) => s.dichteKgM3 },
  { schluessel: 'cp', titel: 'Wärmekapazität', einheit: 'J/(kg·K)', art: 'zahl', nachkomma: 1, wert: (s) => s.waermekapazitaetWsKgK },
  { schluessel: 'nu', titel: 'kin. Viskosität', einheit: 'mm²/s', art: 'zahl', nachkomma: 2, wert: (s) => s.viskositaetKinMm2S,
    inhalt: (s) => (s.viskositaetKinMm2S === null
      ? <StatusBadge klein zustand="warnung" text="nicht belegt"
          titel="Der Wert steht in keiner Quelle; eine Extrapolation wäre eine Schätzung." />
      : formatiereZahl(s.viskositaetKinMm2S, 2)) },
  { schluessel: 'herkunft', titel: 'Herkunft', art: 'status',
    wert: (s) => zustandHerkunft(s.herkunft).text,
    inhalt: (s) => { const z = zustandHerkunft(s.herkunft); return <StatusBadge klein zustand={z.zustand} text={z.text} />; } },
  { schluessel: 'rand', titel: 'Randbedingung', wert: (s) => s.randbedingung ?? '' },
  { schluessel: 'hinweis', titel: 'Hinweis', wert: (s) => s.hinweis ?? '', versteckt: true },
];

const RUECKKUEHLKREIS: Spalte<Kaelterueckkuehlkreis>[] = [
  { schluessel: 'art', titel: 'Kreisart', wert: (k) => k.kreisart },
  { schluessel: 'medium', titel: 'Medium', wert: (k) => k.medium ?? '' },
  { schluessel: 'glykol', titel: 'Glykol', einheit: 'Vol.-%', art: 'zahl', nachkomma: 0, wert: (k) => k.glykolProzent },
  { schluessel: 'cp', titel: 'Wärmekapazität', einheit: 'J/(kg·K)', art: 'zahl', nachkomma: 1, wert: (k) => k.waermekapazitaetWsKgK },
  { schluessel: 'rho', titel: 'Dichte', einheit: 'kg/m³', art: 'zahl', nachkomma: 2, wert: (k) => k.dichteKgM3 },
  { schluessel: 'nu', titel: 'kin. Viskosität', einheit: 'mm²/s', art: 'zahl', nachkomma: 2, wert: (k) => k.viskositaetMm2S },
  { schluessel: 'bei', titel: 'bei', einheit: '°C', art: 'zahl', nachkomma: 1, wert: (k) => k.stoffwertBei },
  { schluessel: 'n', titel: 'Kreise', art: 'zahl', nachkomma: 0, wert: (k) => k.anzahl },
];

const FESTE_BILANZ: Spalte<Kaeltebilanzzeile>[] = [
  { schluessel: 'bilanz', titel: 'Bilanz', wert: (b) => b.bezeichnung },
  { schluessel: 'erf', titel: 'erforderlich', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (b) => b.erforderlichKw },
  { schluessel: 'inst', titel: 'installiert', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (b) => b.installiertKw },
  { schluessel: 'res', titel: 'Reserve', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (b) => b.reserveKw },
  { schluessel: 'kap', titel: 'Kapazität', einheit: 'kWh', art: 'zahl', nachkomma: 0, wert: (b) => b.kapazitaetKwh },
  { schluessel: 'dauer', titel: 'Dauer', einheit: 'h', art: 'zahl', nachkomma: 2, wert: (b) => b.dauerH },
  { schluessel: 'ta', titel: 'Außentemperatur', einheit: '°C', art: 'zahl', nachkomma: 1, wert: (b) => b.aussentemperaturC },
  { schluessel: 'stand', titel: 'Stand', wert: (b) => b.stand ?? '' },
];

const ANNAHMEN: Spalte<KaelteAnnahme>[] = [
  { schluessel: 'parameter', titel: 'Parameter', immerSichtbar: true, wert: (a) => a.parameter },
  { schluessel: 'wert', titel: 'Wert', art: 'zahl', wert: (a) => a.wert,
    inhalt: (a) => (a.wert === null ? (a.textwert ?? '—') : formatiereZahl(a.wert)) },
  { schluessel: 'einheit', titel: 'Einheit', wert: (a) => a.einheit ?? '' },
  { schluessel: 'herkunft', titel: 'Herkunft', art: 'status',
    wert: (a) => zustandHerkunft(a.herkunft).text,
    inhalt: (a) => { const s = zustandHerkunft(a.herkunft); return <StatusBadge klein zustand={s.zustand} text={s.text} />; } },
  { schluessel: 'quelle', titel: 'Quelle/Norm', wert: (a) => a.quelle ?? '' },
  { schluessel: 'bestaetigt', titel: 'Bestätigung', art: 'status',
    wert: (a) => (a.bestaetigt ? 'bestätigt' : 'unbestätigt'),
    inhalt: (a) => (
      <StatusBadge klein zustand={a.bestaetigt ? 'gut' : 'warnung'}
        text={a.bestaetigt ? 'bestätigt' : 'unbestätigt'} />
    ) },
];

/** Eine Kennzahl je Lesart der Erzeugung bzw. für die Rückkühlung. */
function Deckungskarte({ titel, zeile }: { titel: string; zeile: KaelteLaufzeile | undefined }) {
  if (!zeile) return <KpiCard titel={titel} wert={null} vergleich="nicht gerechnet" />;
  const s = zustandBilanz(zeile.stand);
  return (
    <KpiCard titel={titel} wert={zeile.deckungProzent} einheit="%" nachkomma={1}
             zustand={s.zustand === 'info' ? undefined : s.zustand} zustandText={s.text}
             vergleich={`${zeile.bezeichnung}`} />
  );
}

function Bilanzlauf({ lauf }: { lauf: KaelteBilanzlauf }) {
  const zeilen = [...lauf.zeilen].sort((a, b) => a.reihenfolge - b.reihenfolge);
  return (
    <>
      <DataTable name="Bilanzzeilen" spalten={LAUFZEILEN} zeilen={zeilen}
                 schluessel={(z) => `${z.bilanzart}-${z.variante}-${z.reihenfolge}`}
                 suche={false} dicht
                 leer={<EmptyState klein titel="Der Lauf hat keine Zeilen geschrieben." />} />
      {zeilen.filter((z) => z.rechenweg).map((z) => (
        <details key={`weg-${z.bilanzart}-${z.variante}-${z.reihenfolge}`}>
          <summary>Rechenweg: {BILANZART[z.bilanzart] ?? z.bilanzart} · {z.bezeichnung}</summary>
          <p className="schwach"><Zeilen text={z.rechenweg} /></p>
          {z.quellen && <p className="schwach">Quellen: <Zeilen text={z.quellen} /></p>}
        </details>
      ))}
      {lauf.hinweise.length > 0 && (
        <ul className="liste">
          {lauf.hinweise.map((h) => <li key={h}>{h}</li>)}
        </ul>
      )}
    </>
  );
}

function Anlage({ a }: { a: Kaelteanlage }) {
  const beschreibung = [
    a.hersteller && a.typ ? `${a.hersteller} ${a.typ}` : a.hersteller,
    a.kaeltemittel ? `Kältemittel ${a.kaeltemittel}` : null,
    a.geraete > 0
      ? `${a.geraete} Gerät(e) – aus dem Modell gezählt, nicht aus der Liste`
      : 'keine eigenen Geräte im Modell',
    a.standort,
  ].filter(Boolean).join(' · ');

  return (
    <ContentSection titel={`${a.kennung} · ${a.bezeichnung}`} beschreibung={beschreibung}
                    aufklappbar standardOffen={false}>
      {a.hinweis && <p className="schwach">{a.hinweis}</p>}
      <DataTable name={`Kennwerte ${a.kennung}`} spalten={KENNWERTE} zeilen={a.kennwerte}
                 schluessel={(k) => `${k.schluessel}-${k.betriebsfall ?? ''}`}
                 suche={false} dicht
                 leer={<EmptyState klein titel="Keine Kennwerte hinterlegt."
                                   erklaerung="Für diese Anlage liegt kein Datenblatt vor." />} />
      {a.datenblatt && <p className="schwach">Datenblatt: {a.datenblatt}</p>}
    </ContentSection>
  );
}

export function Kaelte() {
  const { api, projekt } = useOutletContext<Rahmen>();
  return <KaelteAnsicht api={api} projektId={projekt.id} />;
}

export function KaelteAnsicht({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [daten, setDaten] = useState<KaelteseiteMitLauf | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setFehler(null);
    try {
      // Die Route liefert seit TH-1 mehr als der Grundtyp in api.ts.
      const antwort = (await api.kaelte(projektId)) as KaelteseiteMitLauf | undefined;
      setDaten(antwort ?? null);
      if (!antwort) setFehler('Die Antwort war leer.');
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, [api, projektId]);

  useEffect(() => { void laden(); }, [laden]);

  const lauf = daten?.lauf ?? null;

  const kopf = (
    <PageHeader
      titel="Kälte"
      beschreibung={'Kältebilanz in drei Lesarten gegen die Kühllast, Rückkühlung, '
        + 'Eisspeicher und freie Kühlung – mit den Randbedingungen ihrer Kennwerte.'}
      meta={daten ? rechenstand(lauf) : undefined}
      aktionen={(
        <ExportLeiste ziele={[
          { art: 'pdf', beschriftung: 'PDF Kältebilanz', laden: () => api.kaeltePdf(projektId),
            gesperrt: lauf ? undefined : { grund: 'Noch kein Kältebilanzlauf – erst „kaelte-bilanz“ rechnen.' } },
          { art: 'excel', laden: () => api.kaelteExcel(projektId),
            gesperrt: lauf || (daten?.anlagen ?? []).length > 0
              ? undefined : { grund: 'Noch kein Kältebilanzlauf und keine Kälteanlage – ohne Bestand gibt es keine Liste.' } },
        ]} />
      )}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState titel="Die Kälteseite konnte nicht geladen werden." meldung={fehler}
                    beiErneutVersuchen={() => { void laden(); }} />
      </>
    );
  }

  if (!daten) return <>{kopf}<LoadingState text="Kälte wird geladen …" /></>;

  const anlagen = daten.anlagen ?? [];
  const annahmen = daten.annahmen ?? [];
  const offenAnnahmen = unbestaetigt(annahmen);
  const zeilen = lauf?.zeilen ?? [];
  const lesart = (v: string) => zeilen.find((z) => z.bilanzart === 'erzeugung' && z.variante === v);
  const rueck = zeilen.find((z) => z.bilanzart === 'rueckkuehlung');
  const offeneBefunde = (lauf?.befunde ?? []).filter((b) => b.stand === 'offen').length
    + (daten.befunde ?? []).filter((f) => f.status === 'offen').length;

  return (
    <>
      {kopf}

      {/* Vorbehalte direkt unter dem Kopf im gemeinsamen Kasten (TX3, S1.2 Nr. 2): Annahmen, offene
          Fachfragen (n−1 HZK-32, DIN EN 378 HZK-31/40 – nicht entschieden, nicht prüfbar), vorläufiger und
          veralteter Lauf. */}
      <Vorbehalte eintraege={kaelteVorbehalte(lauf, daten.massgebendeKuehllast ?? null, offenAnnahmen, annahmen.length)} />
      {daten.massgebendeKuehllast?.hinweis && (
        <p className="schwach">{daten.massgebendeKuehllast.hinweis}</p>
      )}

      {lauf && (
        <KpiReihe>
          <KpiCard titel="Kühllast (Spitze)" wert={lauf.kuehllastSpitzeKw} einheit="kW" nachkomma={1}
                   vergleich={`Kühllast-Lauf ${lauf.kuehllastLaufnummer ?? '—'}`
                     + (lauf.aussentemperaturMaxC !== null
                       ? ` · ${formatiereZahl(lauf.aussentemperaturMaxC, 1)} °C`
                       : '')} />
          <Deckungskarte titel="Erzeugung Lesart A" zeile={lesart('A')} />
          <Deckungskarte titel="Erzeugung Lesart B1" zeile={lesart('B1')} />
          <Deckungskarte titel="Erzeugung Lesart B2" zeile={lesart('B2')} />
          <Deckungskarte titel="Rückkühlung" zeile={rueck} />
          <KpiCard titel="offene Befunde" wert={offeneBefunde}
                   vergleich="Widersprüche der Quellen, nicht entschieden" />
        </KpiReihe>
      )}

      <ContentSection
        titel="Kältebilanz – jüngster Bilanzlauf"
        beschreibung={'Erforderlich gegen installiert in drei Lesarten nebeneinander. Keine Lesart '
          + 'ist „richtig“ markiert; welche gilt, entscheidet die Fachplanung (HZK-06). Der '
          + 'Eisspeicher liefert Leistung nur für begrenzte Dauer und wird in keiner Lesart addiert.'}
        meta={lauf ? lauf.datenstand : undefined}
      >
        {lauf
          ? <Bilanzlauf lauf={lauf} />
          : (
            <EmptyState
              titel="Noch kein Bilanzlauf."
              erklaerung={'Die Kältebilanz entsteht mit dem Werkzeug „kaelte-bilanz <Projekt> '
                + '--schreiben“ gegen den maßgebenden Kühllast-Lauf. Bis dahin gilt nur der feste '
                + 'Stand vom 22.09.2026 unten.'} />
          )}
      </ContentSection>

      <KaelteJahresgangAbschnitt api={api} projektId={projektId} spitzeKw={lauf?.kuehllastSpitzeKw ?? null} />

      {lauf && (
        <ContentSection
          titel="Befunde des Bilanzlaufs"
          beschreibung={'Widersprüche zwischen Datenblatt, Geräteliste und Modell – mit beiden Zahlen '
            + 'und beiden Quellen. Keiner wird hier entschieden.'}
        >
          <DataTable name="Befunde" spalten={LAUFBEFUNDE} zeilen={lauf.befunde}
                     schluessel={(b) => `${b.reihenfolge}-${b.thema}`} suche={false} dicht
                     leer={<EmptyState klein titel="Der Lauf hat keinen Befund geschrieben." />} />
        </ContentSection>
      )}

      {(daten.befunde ?? []).length > 0 && (
        <ContentSection
          titel="Widersprüche aus der Lastrechnung"
          beschreibung="Befunde der Heiz- und Kühllastrechnung im Bereich Kälte."
        >
          <DataTable name="Lastbefunde" spalten={LASTBEFUNDE} zeilen={daten.befunde}
                     schluessel={(f) => f.schluessel} suche={false} dicht />
        </ContentSection>
      )}

      <ContentSection titel="Anlagen"
                      beschreibung={'Erzeugung, Rückkühlung, Eisspeicher und freie Kühlung mit ihren '
                        + 'Kennwerten. Ein Datenblatt ist eine Auslegung, keine Messung.'}>
        {anlagen.length === 0
          ? <EmptyState titel="Keine Kälteanlage erfasst."
                        erklaerung="Für dieses Projekt liegen keine Kälteanlagen vor." />
          : anlagen.map((a) => <Anlage key={a.id} a={a} />)}
      </ContentSection>

      <ContentSection
        titel="Stoffwerte"
        beschreibung={'Ein Stoffwert gilt bei EINER Temperatur. Eine leere Zelle ist eine Aussage: '
          + 'Der Wert steht in keiner Quelle.'}
      >
        <DataTable name="Stoffwerte" spalten={STOFFWERTE} zeilen={daten.stoffwerte ?? []}
                   schluessel={(s) => `${s.fluid}-${s.konzentrationVolProz ?? ''}-${s.temperaturC}`}
                   suche={false} dicht
                   leer={<EmptyState klein titel="Keine Stoffwerte hinterlegt." />} />
      </ContentSection>

      <ContentSection
        titel="Rückkühlkreis – die Gegenprobe"
        beschreibung={'Die Stoffwertsätze im Netzkreis Rückkühlung. Steht hier mehr als eine '
          + 'Konzentration, ist etwas auseinandergelaufen.'}
      >
        <DataTable name="Rückkühlkreis" spalten={RUECKKUEHLKREIS} zeilen={daten.rueckkuehlkreis ?? []}
                   schluessel={(k) => `${k.kreisart}-${k.glykolProzent ?? ''}-${k.stoffwertBei ?? ''}-${k.anzahl}`}
                   suche={false} dicht
                   leer={<EmptyState klein titel="Kein Rückkühlkreis im Netz erkannt." />} />
      </ContentSection>

      <ContentSection
        titel="Feste Bilanz (überholt)"
        beschreibung={daten.festeBilanzStand}
        meta={<StatusBadge klein zustand="warnung" text="überholt" />}
        aufklappbar standardOffen={!lauf}
      >
        <DataTable name="Feste Bilanz" spalten={FESTE_BILANZ} zeilen={daten.bilanz ?? []}
                   schluessel={(b) => `${b.bilanzart}-${b.betriebsfall}`} suche={false} dicht
                   leer={<EmptyState klein titel="Keine festen Zeilen." />} />
      </ContentSection>

      <ContentSection
        titel="Annahmen und Vorgaben"
        beschreibung={'Ansätze der Kälte und der Stoffwerte sowie jeder Kennwert, der nicht aus einem '
          + 'Datenblatt stammt. Unbestätigte stehen oben.'}
      >
        <DataTable name="Annahmen und Vorgaben" spalten={ANNAHMEN} zeilen={annahmen}
                   schluessel={(a) => `${a.parameter}-${a.herkunft}`} suche={annahmen.length > 20} dicht
                   ausfuhr={annahmen.length > 20} ausfuhrDateiname="kaelte-annahmen.csv"
                   leer={<EmptyState klein titel="Keine Annahmen erfasst." />} />
      </ContentSection>
    </>
  );
}

/** Text der Linie „installierte Kälteleistung“ im Jahresgang (Auflage main zu TH-2: kein Auslegungsfall). */
export const GRENZE_KAELTE_INSTALLIERT = 'installierte Kälteleistung (Lesart A); Auslegungsfall siehe Kältebilanz';

/** Text der Linie n−1 – Anforderung offen, nicht bewertet. */
export const GRENZE_KAELTE_N_MINUS_1 = 'n−1 ohne Eisspeicher; Anforderung offen (HZK-32)';

/**
 * Die Einträge des Vorbehaltskastens der Kälteseite: Annahmen, die offenen Fachfragen n−1 (HZK-32) und
 * DIN EN 378 (HZK-31, HZK-40) als „nicht prüfbar“ bzw. „nicht bewertet“, der vorläufige und der veraltete Lauf.
 */
export function kaelteVorbehalte(
  lauf: KaelteBilanzlauf | null, massgebend: KaelteseiteMitLauf['massgebendeKuehllast'] | null,
  offenAnnahmen: number, annahmenGesamt: number,
): Vorbehalt[] {
  const liste: Vorbehalt[] = [];
  if (offenAnnahmen > 0) liste.push(annahmenVorbehalt(offenAnnahmen, annahmenGesamt));
  liste.push(
    { art: 'fachfrage', verweis: 'Kältenachweis (EN 378)',
      text: 'HZK-32: Redundanz n−1 der Kälteerzeugung – ob n−1 gefordert ist, ist offen; n−1 wird nicht bewertet '
        + '– Antwort des Eigentümers steht aus.' },
    { art: 'fachfrage', verweis: 'Kältenachweis (EN 378)',
      text: 'HZK-31, HZK-40: DIN EN 378 – nicht prüfbar; Füllmenge je Kreis, Aufstellraum, Gaswarnung und '
        + 'Notlüftung fehlen, die Normwerte sind unbestätigt.' },
  );
  if (lauf?.kuehllastVorlaeufig) {
    liste.push({ art: 'sonstiges', text: `Kühllast-Lauf ${lauf.kuehllastLaufnummer ?? '—'} ist vorläufig`
      + (lauf.vollstaendigkeitProzent !== null ? ` (Vollständigkeit ${formatiereZahl(lauf.vollstaendigkeitProzent, 1)} %)` : '')
      + '. Die Bilanz steht unter demselben Vorbehalt.' });
  }
  if (bilanzVeraltet(lauf, massgebend ?? null)) {
    liste.push({ art: 'veraltet', text: `Der Bilanzlauf steht auf Kühllast-Lauf ${lauf?.kuehllastLaufnummer ?? '—'}, `
      + `maßgebend ist Lauf ${massgebend?.laufnummer}. Die Bilanz ist mit „kaelte-bilanz“ neu zu rechnen.` });
  }
  return liste;
}

/**
 * Der Kühllast-Jahresgang (TH-2 Stufe C) – eigener Abruf, eigene Zustände. Auflage main: Grundlage
 * benannt, KEIN Auslegungsfall; installierte Leistung und n−1 nur als beschriftete Linien, die
 * Auslegungsgröße (Kältebilanz) steht daneben.
 */
function KaelteJahresgangAbschnitt({ api, projektId, spitzeKw }: {
  api: ApiClient; projektId: string; spitzeKw: number | null;
}) {
  const [werte, setWerte] = useState<KaelteJahresgangwerte | null | undefined>(undefined);
  const [fehler, setFehler] = useState<unknown>(null);

  const laden = useCallback(async () => {
    setFehler(null);
    setWerte(undefined);
    try {
      setWerte((await api.kaelteJahresgang(projektId)) ?? null);
    } catch (e) {
      setFehler(e);
    }
  }, [api, projektId]);

  useEffect(() => { void laden(); }, [laden]);

  const grenzen = [
    ...(werte?.installiertKw != null
      ? [{ wert: werte.installiertKw, titel: GRENZE_KAELTE_INSTALLIERT, zustand: 'info' as const }] : []),
    ...(werte?.nMinus1Kw != null
      ? [{ wert: werte.nMinus1Kw, titel: GRENZE_KAELTE_N_MINUS_1, zustand: 'info' as const }] : []),
  ];

  return (
    <ContentSection titel="Kühllast im Jahresverlauf"
      beschreibung={werte?.grundlage ?? 'Stundenbilanz der Kühllast zum maßgebenden Kühllast-Lauf – kein Auslegungsfall.'}>
      <p className="hinweis" role="note" data-auslegung="">
        Auslegungsgröße: Kühllastspitze {formatiereZahl(spitzeKw, 1)} kW gegen {formatiereZahl(werte?.installiertKw ?? null, 1)} kW
        {' '}installiert (Lesart A) – die drei Lesarten und ihre Vorbehalte stehen in der Kältebilanz oben; n−1 und
        DIN EN 378 sind nicht bewertet (HZK-32, HZK-31/40).
      </p>
      <Jahresgang
        titel={werte ? `Kühllast je Stunde, Kühllast Lauf ${werte.laufnummer}` : 'Kühllast je Stunde'}
        einheit="kW" achsenTitel="Kühllast (trocken)" nachkomma={1}
        werte={werte?.lastKw ?? []}
        zeitpunkt={(i) => werte?.zeitpunkte?.[i] ?? String(i + 1)}
        grenzen={grenzen}
        laedt={werte === undefined && !fehler}
        fehler={fehler ?? undefined}
        beiErneutVersuchen={() => { void laden(); }}
        leer={<EmptyState klein titel="Kein Jahresgang zum maßgebenden Kühllast-Lauf."
          erklaerung="Die Jahressimulation (Bahn TL) ist für diesen Lauf nicht gerechnet, oder es gibt keinen maßgebenden Kühllast-Lauf." />}
      />
    </ContentSection>
  );
}
